import React, { useContext, useMemo } from 'react';
import {
  Col,
  ConfigProvider,
  Flex,
  Menu,
  MenuProps,
  Row,
  Space,
  Typography,
} from 'antd';
import { createStyles } from 'antd-style';

import MainMenuContext from '@totem/components/ui_components/menus/menuContext';
import { IBMenuItem } from '@totem/components/ui_components/menus/types';
import { createMenuLabel } from '@totem/components/ui_components/menus/utilities';
import { isNotNull, isNull } from '@totem/utilities/common';
const { Title, Paragraph } = Typography;

const useStyles = createStyles(({ token }) => ({
  navigationPopup: {
    padding: token.padding,
    minWidth: 480,
    background: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
  },
  menuItem: {
    borderRadius: token.borderRadius,
    transition: `all ${token.motionDurationSlow}`,
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.02)',
    },
  },
  menuItemSpace: {
    padding: token.paddingSM,
  },
  leadingHeader: {
    margin: '0 !important',
    paddingBottom: token.paddingXS,
    borderBottom: `1px solid ${token.colorSplit}`,
    '& .ant-menu-item-icon': {
      marginRight: token.marginSM,
    },
  },
  marginLess: {
    margin: '0 !important',
  },
}));

const MenuItem = ({
  title,
  description,
  icon,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  icon: React.ReactNode;
}) => {
  const { styles } = useStyles();

  const sizedIcon = React.isValidElement(icon)
    ? React.cloneElement(
        icon as React.ReactElement<{ style?: React.CSSProperties }>,
        {
          style: { fontSize: 40 },
        },
      )
    : icon;

  return (
    <div className={styles.menuItem}>
      <Space vertical={false} size={4}>
        <div>{sizedIcon}</div>
        <Space vertical size={4} className={styles.menuItemSpace}>
          <Title level={5} className={styles.marginLess}>
            {title}
          </Title>
          <Paragraph type="secondary" className={styles.marginLess}>
            {description}
          </Paragraph>
        </Space>
      </Space>
    </div>
  );
};

const mapMenuItems = (
  items: IBMenuItem[],
  depth: number,
): MenuProps['items'] => {
  return items.map((item: IBMenuItem) => {
    return {
      key: item.key,
      title: item.title,
      label:
        depth === 0
          ? createMenuLabel(item, item.label)
          : createMenuLabel(
              item,
              <MenuItem
                title={item.label}
                description={item.description}
                icon={item.icon}
              />,
            ),
      icon: depth === 0 ? item.icon : null,
      children:
        isNotNull(item.children) && item.children.length > 0
          ? mapMenuItems(item.children, depth + 1)
          : null,
    };
  });
};

const HeaderMenu = () => {
  const { mainMenu } = useContext(MainMenuContext);

  const antMenuItems: MenuProps['items'] = useMemo(() => {
    if (isNull(mainMenu) || mainMenu.length === 0) return [];
    return mapMenuItems(mainMenu, 0);
  }, [mainMenu]);

  const { styles } = useStyles();
  const popupRender: MenuProps['popupRender'] = (_, { item }) => {
    return (
      <Flex className={styles.navigationPopup} vertical gap="medium">
        <Typography.Title level={3} className={styles.leadingHeader}>
          {item.title}
        </Typography.Title>
        <Row gutter={16}>
          {React.Children.map(item.children as React.ReactNode, (child) => {
            if (!React.isValidElement(child)) {
              return null;
            }
            return (
              <Col span={12} key={child.key}>
                {child}
              </Col>
            );
          })}
        </Row>
      </Flex>
    );
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            popupBg: '#fff',
            horizontalItemSelectedColor: '#1677ff',
            horizontalItemHoverColor: '#1677ff',
          },
          Typography: {
            titleMarginBottom: 0,
            titleMarginTop: 0,
          },
        },
      }}
    >
      <Menu
        mode="horizontal"
        items={antMenuItems}
        popupRender={popupRender}
        style={{ flex: 1 }}
      />
    </ConfigProvider>
  );
};

export default HeaderMenu;
